import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { HashRouter as Router } from "react-router-dom";
import { useAuth } from "@sal/react.authentication";
import { useBooking } from "../components/BookingContextProvider";
import { servicePath, transitionDurationSpinners, sessionLogoutUrl, sessionExpiredUrl, sessionExpiredTime, fullTimeFormat } from "../components/Settings";
import { createMuiTheme, ThemeProvider, AppBar, Toolbar, Button, Grid, Backdrop, CircularProgress, Typography, Icon, capitalize, Box, Link } from "@material-ui/core";
import { get } from "../code/api";
import { redirectToDashboard, urlRedirect } from "../code/common";
import { Error } from "../components/Error";
import Routes from "../routes";
import logo from "../images/Interislander_Freight_Transparent_Logo.png";

export default function landing() {
	const { booking, setBooking } = useBooking();
	const [selectedButton, setSelectedButton] = useState<string[]>(["", "", "", ""]);
	const [loading, setLoading] = useState(true);
	const auth = useAuth();
	const queryParams = new URLSearchParams(window.location.search);
	const jwt = queryParams ? queryParams.get("id_token") : null;
	const jsonState = queryParams ? queryParams.get("state") : null;
	const state = queryParams && jsonState ? JSON.parse(jsonState) : null;
	const landingError = queryParams ? queryParams.get("landingError") : null;
	const fillColor: string = "#FFFFFF";
	const outlineColor: string = "#000000";
	let lastActivityTime = dayjs();
	let copyrightYear = dayjs().year();

	if (window.location.hash === "#/" && (!jwt || !state)) {
		return <Error errorMessage="Jwt or State not provided in the URL." />;
	} else if (landingError) {
		return <Error errorMessage={landingError} />;
	}

	const theme = createMuiTheme({
		palette: {
			primary: {
				main: "#f37021"	// KiwiRail ORANGE
			},
			secondary: {
				main: "#006ca5"	// KiwiRail BLUE
			}
		},
		overrides: {
			MuiFormLabel: {
				root: {
					color: "#000000"
				}
			},
			MuiInputBase: {
				input: {
					paddingBottom: "6px"
				}
			},
			MuiSelect: {
				selectMenu: {
					height: "18.4px"
				}
			},
			MuiBackdrop: {
				root: {
					zIndex: 1
				}
			},
			MuiTooltip: {
				tooltip: {
					maxWidth: "inherit"
				}
			},
			MuiButton: {
				containedPrimary: {
					color: fillColor
				},
				root: {
					textTransform: "none"
				}
			},
			MuiStepIcon: {
				text: {
					fill: fillColor
				}
			},
			MuiInput: {
				underline: {
					"&:after": {
						borderBottom: "2px solid",
						borderBottomColor: outlineColor
					}
				}
			},
			MuiOutlinedInput: {
				root: {
					"&.Mui-focused ": {
						"&.MuiOutlinedInput": {
							"&-notchedOutline": {
								borderColor: outlineColor
							}
						}
					}
				}
			},
			MuiChip: {
				label: {
					whiteSpace: "break-spaces"
				}
			}
		}
	});

	const handleNavButtonClick = (index: number) => {
		let selectedArray: Array<string> = ["", "", "", ""];

		if (index >= 0) {
			selectedArray[index] = " selected";
		}

		setSelectedButton(selectedArray);

		switch (index) {
			case -1:
				window.location.replace(window.location.origin);
				break;
			case 0:
				redirectToDashboard(auth, booking, setLoading);
				break;
			case 1:
				window.location.hash = "bookingDetails";
				break;
			case 2:
				window.location.replace(window.location.origin);
				break;
			case 3:
				window.open("https://www.kiwirailfreight.co.nz/contact-us/", "_blank");
				break;
			case 4:
				redirectToDashboard(auth, booking, setLoading, sessionLogoutUrl);
				break;
		}
	};

	const resetUserActivityTimeout = (time: number) => {
		window.setTimeout(() => afterTimeoutAction(), time);
	};

	const afterTimeoutAction = () => {
		//if user remains inactive through timer
		if (lastActivityTime.add(sessionExpiredTime, "millisecond").format(fullTimeFormat) === dayjs().format(fullTimeFormat)) {
			get(servicePath + "settings").then(response => {
				window.location.href = response.crmDashboadBaseUrl + sessionExpiredUrl;
			});
		} else {
			//if user became active when timer was running - updte the timer
			let timeoutTime = lastActivityTime.add(sessionExpiredTime, "millisecond");
			resetUserActivityTimeout(timeoutTime.diff(dayjs()));
		}
	};

	const activateActivityTracker = () => {
		window.addEventListener("click", () => onActivityTemp());
		window.addEventListener("keypress", () => onActivityTemp());
	};

	const onActivityTemp = () => {
		lastActivityTime = dayjs();
	};

	useEffect(() => {
		setLoading(true);
		activateActivityTracker();
		resetUserActivityTimeout(sessionExpiredTime);

		if (jwt) {
			auth.thirdPartyLogin(() => { });
		} else {
			urlRedirect(jwt, state, setLoading);
		}
	}, []);

	return <ThemeProvider theme={theme}>
		<link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
		<div id="wrapper">
			<header>
				<Grid container className="user-info-bar" justify="flex-end">
					<Grid item>
						<Grid container spacing={1} justify="space-evenly">
							<Grid item className="cursor-default">
								<Icon>person</Icon>
							</Grid>
							<Grid item className="cursor-default">
								<Typography>{auth.user}</Typography>
							</Grid>
							<Grid item className="cursor-default">
								<Typography>|</Typography>
							</Grid>
							<Grid item className="cursor-pointer" onClick={() => handleNavButtonClick(4)}>
								<Typography>{auth.isLoggedIn ? "Log out" : "Log in"}</Typography>
							</Grid>
							<Grid item className="cursor-pointer" onClick={() => handleNavButtonClick(4)}>
								<Icon>logout</Icon>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<AppBar className="navigation-bar" position="static">
					<Toolbar>
						<Grid container justify="space-between" alignItems="center">
							<Grid item>
								<img src={logo} className="interislander-logo" />
							</Grid>
							<Grid item hidden={auth.isLoggedIn}>
								<Button color="inherit" className="navigation-button">Logged out</Button>
							</Grid>
							<Grid item hidden={!auth.isLoggedIn}>
								<Grid container justify="flex-start">
									<Grid item>
										<Button color="inherit" className={"navigation-button" + selectedButton[0]} onClick={() => handleNavButtonClick(0)}>Dashboard</Button>
									</Grid>
									<Grid item>
										<Button color="inherit" className={"navigation-button" + (window.location.hash.indexOf("bookingDetails") > -1 ? " selected" : "")} onClick={() => handleNavButtonClick(1)}>{booking.isChangeBooking ? "Change" : "New"} booking</Button>
									</Grid>
									<Grid item hidden>
										<Button color="inherit" className={"navigation-button" + selectedButton[2]} onClick={() => handleNavButtonClick(2)}>Dangerous goods</Button>
									</Grid>
									<Grid item>
										<Button color="inherit" className={"navigation-button" + selectedButton[3]} onClick={() => handleNavButtonClick(3)}>Contact</Button>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Toolbar>
				</AppBar>
			</header>
			<div id="content">
				<Router basename="/">
					<Routes />
				</Router>
				<Backdrop open={loading} transitionDuration={{ enter: transitionDurationSpinners, exit: transitionDurationSpinners }}>
					<CircularProgress color="secondary" />
				</Backdrop>
			</div>
			<footer className="footer">
				<Box className="footer-row">
					<Grid container justify="space-between" className="footer-links">
						<Grid item>
							<Link href="https://www.kiwirailfreight.co.nz/terms-and-conditions/" target="_blank" className="footer-links-text"><strong>Terms</strong></Link>
							<Link href="https://www.kiwirailfreight.co.nz/privacy/" target="_blank" className="footer-links-text"><strong>Privacy</strong></Link>
						</Grid>
						<Grid item>
							<p>Copyright &copy; {copyrightYear} KiwiRail</p>
							{/*<p className="footer-grey-text">SSO v1.7 17/08/2021</p>*/}
						</Grid>
					</Grid>
				</Box>
			</footer>
		</div>
	</ThemeProvider>;
}